import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { getTenantFromPath, getTenantSubdomain, isValidTenantId } from './lib/tenant';
import { useAuthStore } from './stores/authStore';

// Pages
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import AdminDashboard from './pages/AdminDashboard';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import EmployeePortal from './pages/EmployeePortal';
import Students from './pages/Students';
import Classes from './pages/Classes';
import Enrollments from './pages/Enrollments';

import Payments from './pages/Payments';
import Communications from './pages/Communications';
import Settings from './pages/Settings';
import TitleSettings from './pages/settings/TitleSettings';
import LinkedClasses from './pages/LinkedClasses';
import Grading from './pages/Grading';
import ParentImport from './pages/ParentImport';
import HiringPipeline from './pages/hiring/HiringPipeline';
import ApplicationDetail from './pages/hiring/ApplicationDetail';
import LeadDetail from './pages/crm/LeadDetail';
import CRMPipeline from './pages/crm/CRMPipeline';
import LeadForm from './pages/crm/LeadForm';
import CRMReports from './pages/crm/CRMReports';
import AttendanceDashboard from './pages/attendance/AttendanceDashboard';
import ClassAttendance from './pages/attendance/ClassAttendance';
import Locations from './pages/Locations';

// Components
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import TenantRoute from './components/TenantRoute';

/**
 * Get the default route based on user role
 */
const getDefaultRoute = (user, tenantId = null) => {
  if (!user) return tenantId ? `/${tenantId}/login` : '/login';
  
  const basePath = tenantId ? `/${tenantId}` : '';
  
  switch (user.role) {
    case 'super_admin':
      return tenantId ? `${basePath}/dashboard` : '/super-admin';
    case 'admin':
    case 'parent':
    case 'employee':
      return `${basePath}/dashboard`;
    default:
      return tenantId ? `/${tenantId}/login` : '/login';
  }
};

function App() {
  const { user, isAuthenticated } = useAuthStore();
  
  // Get tenant from path (new approach)
  const pathTenant = getTenantFromPath();
  
  // Fallback to subdomain for backward compatibility
  const subdomainTenant = getTenantSubdomain();
  
  // Use path tenant if available, otherwise subdomain
  const currentTenant = pathTenant || subdomainTenant;



  return (
    <Routes>
      {/* Global Public Routes (no tenant) */}
      <Route path="/signup" element={
        isAuthenticated ? <Navigate to={getDefaultRoute(user, user?.tenantId || currentTenant)} /> : <Signup />
      } />
      <Route path="/forgot-password" element={
        isAuthenticated ? <Navigate to={getDefaultRoute(user, user?.tenantId || currentTenant)} /> : <ForgotPassword />
      } />
      
      {/* Global Dashboard for Admins (no tenant context) */}
      <Route path="/dashboard/*" element={
        <ProtectedRoute allowedRoles={['admin']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<Students />} />
        <Route path="classes" element={<Classes />} />
        <Route path="enrollments" element={<Enrollments />} />
        <Route path="payments" element={<Payments />} />
        <Route path="communications" element={<Communications />} />
        <Route path="grading" element={<Grading />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/titles" element={<TitleSettings />} />
        <Route path="linked-classes" element={<LinkedClasses />} />
        <Route path="hiring" element={<HiringPipeline />} />
        <Route path="hiring/applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="crm/leads/:leadId" element={<LeadDetail />} />
        <Route path="crm/pipeline" element={<CRMPipeline />} />
        <Route path="crm/leads/new" element={<LeadForm />} />
        <Route path="crm/leads/:leadId/edit" element={<LeadForm />} />
        <Route path="crm/reports" element={<CRMReports />} />
        <Route path="users/import" element={<ParentImport />} />
        <Route path="attendance/dashboard" element={<AttendanceDashboard />} />
        <Route path="attendance/class/:classId" element={<ClassAttendance />} />
        <Route path="attendance" element={<Navigate to="attendance/dashboard" replace />} />
        <Route path="locations" element={<Locations />} />
      </Route>

      {/* Super Admin Portal (no tenant context) */}
      <Route path="/super-admin/*" element={
        <ProtectedRoute allowedRoles={['super_admin']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<SuperAdminDashboard />} />
        <Route path="students" element={<Students />} />
        <Route path="classes" element={<Classes />} />
        <Route path="enrollments" element={<Enrollments />} />
        <Route path="payments" element={<Payments />} />
        <Route path="communications" element={<Communications />} />
        <Route path="grading" element={<Grading />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/titles" element={<TitleSettings />} />
        <Route path="linked-classes" element={<LinkedClasses />} />
        <Route path="hiring" element={<HiringPipeline />} />
        <Route path="hiring/applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="crm/leads/:leadId" element={<LeadDetail />} />
        <Route path="crm/pipeline" element={<CRMPipeline />} />
        <Route path="crm/leads/new" element={<LeadForm />} />
        <Route path="crm/leads/:leadId/edit" element={<LeadForm />} />
        <Route path="crm/reports" element={<CRMReports />} />
        <Route path="users/import" element={<ParentImport />} />
        <Route path="attendance/dashboard" element={<AttendanceDashboard />} />
        <Route path="attendance/class/:classId" element={<ClassAttendance />} />
        <Route path="attendance" element={<Navigate to="attendance/dashboard" replace />} />
        <Route path="locations" element={<Locations />} />
      </Route>

      {/* Tenant-specific Routes */}
      <Route path="/:tenantId/*" element={
        <TenantRoute>
          <TenantRoutes />
        </TenantRoute>
      } />

      {/* Global Login (no tenant context) */}
      <Route path="/login" element={
        isAuthenticated ? <Navigate to={getDefaultRoute(user, currentTenant)} /> : <Login />
      } />

      {/* Root redirect */}
      <Route path="/" element={
        isAuthenticated 
          ? <Navigate to={getDefaultRoute(user, currentTenant)} />
          : <Navigate to={currentTenant ? `/${currentTenant}/login` : '/login'} />
      } />

      {/* Catch all */}
      <Route path="*" element={
        isAuthenticated 
          ? <Navigate to={getDefaultRoute(user, currentTenant)} />
          : <Navigate to={currentTenant ? `/${currentTenant}/login` : '/login'} />
      } />
    </Routes>
  );
}

/**
 * Tenant-specific routes component
 * Handles all routes within a tenant context (e.g., /tianjin/*)
 */
function TenantRoutes() {
  const { tenantId } = useParams();
  const { user, isAuthenticated } = useAuthStore();

  // Validate tenant ID format
  if (!isValidTenantId(tenantId)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-50 via-fuchsia-50 to-purple-100">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-purple-900 mb-4">Invalid URL</h1>
          <p className="text-gray-600 mb-6">The organization code is not valid.</p>
          <a href="/" className="text-purple-600 hover:text-purple-700 font-medium">
            Go to ClassroomDragon
          </a>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Tenant Login */}
      <Route path="login" element={
        isAuthenticated ? <Navigate to={`/${tenantId}/dashboard`} /> : <Login />
      } />

      {/* Tenant Dashboard Routes (All roles - shows different view based on role) */}
      <Route path="dashboard/*" element={
        <ProtectedRoute allowedRoles={['admin', 'super_admin', 'parent', 'employee']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<AdminDashboard />} />
        <Route path="students" element={<Students />} />
        <Route path="classes" element={<Classes />} />
        <Route path="enrollments" element={<Enrollments />} />
        <Route path="payments" element={<Payments />} />
        <Route path="communications" element={<Communications />} />
        <Route path="settings" element={<Settings />} />
        <Route path="settings/titles" element={<TitleSettings />} />
        <Route path="hiring" element={<HiringPipeline />} />
        <Route path="hiring/applications/:applicationId" element={<ApplicationDetail />} />
        <Route path="crm/pipeline" element={<CRMPipeline />} />
        <Route path="crm/leads/new" element={<LeadForm />} />
        <Route path="crm/leads/:leadId/edit" element={<LeadForm />} />
        <Route path="crm/reports" element={<CRMReports />} />
        <Route path="crm/leads/:leadId" element={<div>Lead Detail - Coming Soon</div>} />
        <Route path="users/import" element={<ParentImport />} />
        {/* Attendance Routes */}
        <Route path="attendance/dashboard" element={<AttendanceDashboard />} />
        <Route path="attendance/class/:classId" element={<ClassAttendance />} />
        <Route path="attendance" element={<Navigate to="attendance/dashboard" replace />} />
        {/* Locations */}
        <Route path="locations" element={<Locations />} />
      </Route>

      {/* Employee Routes */}
      <Route path="employee/*" element={
        <ProtectedRoute allowedRoles={['employee']}>
          <Layout />
        </ProtectedRoute>
      }>
        <Route index element={<EmployeePortal />} />
        <Route path="classes" element={<Classes />} />
        <Route path="communications" element={<Communications />} />
        {/* Attendance Routes */}
        <Route path="attendance/dashboard" element={<AttendanceDashboard />} />
        <Route path="attendance/class/:classId" element={<ClassAttendance />} />
        <Route path="attendance" element={<Navigate to="attendance/dashboard" replace />} />
      </Route>

      {/* Default redirect within tenant */}
      <Route path="*" element={
        isAuthenticated 
          ? <Navigate to={getDefaultRoute(user, tenantId)} />
          : <Navigate to={`/${tenantId}/login`} />
      } />
    </Routes>
  );
}

export default App;
